import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { FrameworkId } from '@seedgovernance/shared';
import { Welcome } from './Welcome';
import { RegionConfirm } from './RegionConfirm';
import { FrameworkSelect } from './FrameworkSelect';

const STEPS = ['Welcome', 'Region', 'Frameworks', 'Team', 'Assessment', 'Tour', 'Complete'];

export function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    companyName: '',
    workspaceName: '',
    region: '',
    frameworks: [] as FrameworkId[],
  });

  function handleComplete() {
    navigate('/', { replace: true });
  }

  return (
    <div className="min-h-screen bg-surface-dark flex flex-col">
      {/* Progress bar */}
      <div className="border-b border-border bg-surface-mid">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider">
              Step {step + 1} of {STEPS.length}
            </p>
            <p className="text-xs text-slate-500">{STEPS[step]}</p>
          </div>
          <div className="h-1 bg-surface-dark rounded-full overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 flex items-center justify-center p-6">
        {step === 0 && (
          <Welcome
            onNext={({ companyName, workspaceName }) => {
              setData((d) => ({ ...d, companyName, workspaceName }));
              setStep(1);
            }}
          />
        )}

        {step === 1 && (
          <RegionConfirm
            selectedRegion={data.region}
            onNext={(region) => {
              setData((d) => ({ ...d, region }));
              setStep(2);
            }}
            onBack={() => setStep(0)}
          />
        )}

        {step === 2 && (
          <FrameworkSelect
            maxFrameworks={3}
            preselected={data.frameworks}
            onNext={(frameworks) => {
              setData((d) => ({ ...d, frameworks }));
              setStep(3);
            }}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && (
          <PlaceholderStep
            title="Invite Your Team"
            description="Invite team members by email and assign roles. You can do this later from Settings."
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
            skippable
          />
        )}

        {step === 4 && (
          <PlaceholderStep
            title="Import Assessment"
            description="If you completed the Quick Assessment on our website, we can import your results."
            onNext={() => setStep(5)}
            onBack={() => setStep(3)}
            skippable
          />
        )}

        {step === 5 && (
          <PlaceholderStep
            title="Your First Deliverable"
            description="Let's walk through one compliance deliverable end-to-end: assess, complete, export."
            onNext={() => setStep(6)}
            onBack={() => setStep(4)}
            skippable
          />
        )}

        {step === 6 && (
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-growth/10 flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-growth" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20,6 9,17 4,12" />
              </svg>
            </div>
            <h1 className="text-2xl font-display font-bold text-white mb-2">
              You're All Set!
            </h1>
            <p className="text-slate-400 mb-8">
              Your workspace <span className="text-white font-semibold">{data.companyName}</span> is ready.
              Start building your compliance posture.
            </p>
            <button
              onClick={handleComplete}
              className="px-8 py-3 bg-brand hover:bg-brand-dark text-white font-semibold rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PlaceholderStep({
  title,
  description,
  onNext,
  onBack,
  skippable,
}: {
  title: string;
  description: string;
  onNext: () => void;
  onBack: () => void;
  skippable?: boolean;
}) {
  return (
    <div className="max-w-md mx-auto text-center">
      <h1 className="text-2xl font-display font-bold text-white mb-2">
        {title}
      </h1>
      <p className="text-slate-400 mb-8">{description}</p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-surface-light hover:bg-border text-white font-semibold rounded-lg border border-border transition-colors"
        >
          Back
        </button>
        {skippable && (
          <button
            onClick={onNext}
            className="px-6 py-3 text-slate-400 hover:text-white font-semibold transition-colors"
          >
            Skip
          </button>
        )}
        <button
          onClick={onNext}
          className="px-6 py-3 bg-brand hover:bg-brand-dark text-white font-semibold rounded-lg transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
